const FONT_CATALOG = {
    "Manrope": "https://fonts.googleapis.com/css2?family=Manrope:wght@400;600;700;800&display=swap",
    "Poppins": "https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap",
    "Montserrat": "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap",
    "Raleway": "https://fonts.googleapis.com/css2?family=Raleway:wght@400;600;700&display=swap",
    "Nunito": "https://fonts.googleapis.com/css2?family=Nunito:wght@400;700;800&display=swap",
    "Inter": "https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap",
    "Source Sans 3": "https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@400;600;700&display=swap",
    "Quicksand": "https://fonts.googleapis.com/css2?family=Quicksand:wght@400;600;700&display=swap",
    "Josefin Sans": "https://fonts.googleapis.com/css2?family=Josefin+Sans:wght@400;600;700&display=swap",
    "Lato": "https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap",

    "Playfair Display": "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&display=swap",
    "Lora": "https://fonts.googleapis.com/css2?family=Lora:wght@400;700&display=swap",
    "Merriweather": "https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700&display=swap",
    "Cormorant Garamond": "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&display=swap",
    "Libre Baskerville": "https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&display=swap",
    "DM Serif Display": "https://fonts.googleapis.com/css2?family=DM+Serif+Display&display=swap",
    "Cinzel": "https://fonts.googleapis.com/css2?family=Cinzel:wght@400;700&display=swap",
    "Bitter": "https://fonts.googleapis.com/css2?family=Bitter:wght@400;700&display=swap",
    "Abril Fatface": "https://fonts.googleapis.com/css2?family=Abril+Fatface&display=swap",

    "Dancing Script": "https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&display=swap",
    "Great Vibes": "https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap",
    "Pacifico": "https://fonts.googleapis.com/css2?family=Pacifico&display=swap",
    "Sacramento": "https://fonts.googleapis.com/css2?family=Sacramento&display=swap",
    "Amatic SC": "https://fonts.googleapis.com/css2?family=Amatic+SC:wght@700&display=swap",
    // NEW
    "Fredoka": "https://fonts.googleapis.com/css2?family=Fredoka:wght@400;600;700&display=swap",
    "Baloo 2": "https://fonts.googleapis.com/css2?family=Baloo+2:wght@400;600;700&display=swap",
    "Rubik": "https://fonts.googleapis.com/css2?family=Rubik:wght@400;500;700&display=swap",
    "Barlow": "https://fonts.googleapis.com/css2?family=Barlow:wght@400;600;700&display=swap",
    "Indie Flower": "https://fonts.googleapis.com/css2?family=Indie+Flower&display=swap",
    "Shadows Into Light": "https://fonts.googleapis.com/css2?family=Shadows+Into+Light&display=swap",
    "Lobster Two": "https://fonts.googleapis.com/css2?family=Lobster+Two:wght@400;700&display=swap",
    "Chewy": "https://fonts.googleapis.com/css2?family=Chewy&display=swap"
};

// Lightweight helpers for picking and applying a random sample font (one per session)
const FONT_KEYS = Object.freeze(Object.keys(FONT_CATALOG));

function _randFrom(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

/**
 * Pick a random family from the catalog, optionally excluding some.
 * Returns a family name string.
 */
export function pickRandomFont(exclude = []) {
  const excl = new Set((exclude || []).map(String));
  const pool = FONT_KEYS.filter(f => !excl.has(f));
  return pool.length ? _randFrom(pool) : _randFrom(FONT_KEYS);
}

/**
 * Ensures a single random font is used for samples during the current session only.
 * This avoids loading multiple webfonts and keeps things snappy.
 *
 * @param {HTMLElement|null} targetEl - optional element to apply the font to
 * @param {Object} opts
 * @param {string} opts.storageKey - sessionStorage key to persist chosen family
 * @param {string[]} opts.exclude - font families to exclude from random choice
 * @returns {Promise<string>} - the chosen family
 */
export async function ensureRandomSampleFont(targetEl = null, { storageKey = 'ea-sample-font', exclude = [] } = {}) {
  try {
    let family = sessionStorage.getItem(storageKey);
    if (!family) {
      family = pickRandomFont(exclude);
      sessionStorage.setItem(storageKey, family);
    }
    await ensureFontLoaded(family);
    if (targetEl && targetEl.style) {
      targetEl.style.fontFamily = `"${family}", sans-serif`;
    }
    return family;
  } catch (_) {
    // Fallback to body font on any error
    return 'Manrope';
  }
}

export async function ensureFontLoaded(family) {
    if (!family || family === "Arial") return;

    // 1) Preconnect once
    if (!document.querySelector('link[href="https://fonts.googleapis.com"]')) {
        const l1 = document.createElement('link');
        l1.rel = 'preconnect';
        l1.href = 'https://fonts.googleapis.com';
        document.head.appendChild(l1);
    }
    if (!document.querySelector('link[href="https://fonts.gstatic.com"]')) {
        const l2 = document.createElement('link');
        l2.rel = 'preconnect';
        l2.href = 'https://fonts.gstatic.com';
        l2.crossOrigin = 'anonymous';
        document.head.appendChild(l2);
    }

    // 2) Inject the stylesheet link if missing
    const id = `gf-${family.replace(/\s+/g, '-').toLowerCase()}`;
    if (!document.getElementById(id)) {
        const href = FONT_CATALOG[family];
        if (!href) return; // unknown family
        const link = document.createElement('link');
        link.id = id;
        link.rel = 'stylesheet';
        link.href = href;               // many of your URLs already have display=swap
        document.head.appendChild(link);
    }

    // 3) Now wait for the actual face to be usable.
    // Passing a test string reduces false positives.
    try {
        if (document.fonts?.load) {
            await document.fonts.load(`16px "${family}"`, 'BESgxy'); // sample glyphs
            await document.fonts.ready;
        }
    } catch { /* ignore */ }
}